/**
 * The public site's data (the user, 2026-10-14) — what a SIGNED-OUT visitor is
 * shown on the master welcome (`GET /public/home`) and on a rave's own landing
 * page (`GET /public/rave`). Shapes are the contract in
 * `@myrave/shared/publicSite`; every name, number and time on those pages
 * comes from here or is not shown.
 */
import { useQuery } from '@tanstack/react-query';
import type { HeroLine, PublicHome, PublicPeople, PublicRave } from '@myrave/shared/publicSite';
import { INVITE_PARAM, inviteCodeIn } from '@myrave/shared/publicSite';
import { languageOf } from '@myrave/shared';
import { api } from './api';
import { useLocale } from '@/app/LocaleProvider';

export const PUBLIC_HOME_KEY = ['public', 'home'] as const;

/**
 * The page's locale on the request itself, and its LANGUAGE in the query key:
 * the hero lines come back in it (2026-10-27), and the first request can leave
 * before `<html lang>` is set. Switching language keeps the page drawn
 * (`placeholderData`) while the new words arrive.
 */
function useLanguageOfPage(): { language: string; headers: Record<string, string> } {
  const { locale } = useLocale();
  return { language: languageOf(locale), headers: { 'x-myrave-locale': locale } };
}

export function usePublicHome() {
  const { language, headers } = useLanguageOfPage();
  return useQuery({
    queryKey: [...PUBLIC_HOME_KEY, language],
    queryFn: () => api.get<PublicHome>('/public/home', headers),
    placeholderData: (previous) => previous,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** The invite code in this page's address, when it is one. */
export function inviteCodeFromUrl(search = typeof window === 'undefined' ? '' : window.location.search): string | null {
  // A shared link may arrive with the message glued on ("<code> Come to …").
  return inviteCodeIn(new URLSearchParams(search).get(INVITE_PARAM));
}

/** `rave`: the public page on the main domain names its rave (`<platform>/<rave>/<edition>`, 2026-10-27). */
export function publicRavePath(editionSlug: string | null, invite: string | null, rave: string | null = null): string {
  const q = new URLSearchParams();
  if (invite) q.set(INVITE_PARAM, invite);
  if (editionSlug) q.set('edition', editionSlug);
  if (rave) q.set('rave', rave);
  const s = q.toString();
  return s ? `/public/rave?${s}` : '/public/rave';
}

export function usePublicRave(editionSlug: string | null, invite: string | null, rave: string | null = null, enabled = true) {
  const { language, headers } = useLanguageOfPage();
  return useQuery({
    queryKey: ['public', 'rave', rave, editionSlug, invite, language],
    queryFn: () => api.get<PublicRave>(publicRavePath(editionSlug, invite, rave), headers),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** The hero's two lines, in the reader's language. */
export interface HeroPair { line1: string; line2: string }

/**
 * One line, at random, per page load. In the page's language when the server
 * sent that pair (`text1`/`text2` in `lang`), Hebrew from `he1`/`he2` for
 * an older answer, English otherwise. `null` when the list is empty — the
 * page then uses its own built-in pair.
 */
export function pickHeroLine(lines: readonly HeroLine[] | undefined, locale: string, random = Math.random): HeroPair | null {
  if (!lines || lines.length === 0) return null;
  const line = lines[Math.min(lines.length - 1, Math.floor(random() * lines.length))]!;
  // By LANGUAGE: since step 73 the locale is a full tag (`he-IL`, `he-US`).
  const language = languageOf(locale);
  if (language !== 'en' && line.lang === language && line.text1 && line.text2) return { line1: line.text1, line2: line.text2 };
  if (language === 'he' && line.he1 && line.he2) return { line1: line.he1, line2: line.he2 };
  return { line1: line.line1, line2: line.line2 };
}

/**
 * "People using MyRave" at the foot of every page (the user, 2026-10-27).
 * The server decides the number (`GET /public/people` — made up for now);
 * nothing until it answers, and nothing offline.
 */
export function usePeopleCount(): number | null {
  const q = useQuery({
    queryKey: ['public', 'people'],
    queryFn: () => api.get<PublicPeople>('/public/people'),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return typeof q.data?.people === 'number' ? q.data.people : null;
}
